/**
 * web/src/screens/Notebook/giftflow/home.tsx — Gift home (Notebook · Gift,
 * title Gifts): the primary "Get a gift for {partner}" card, the in-progress
 * block (which reopens Plan, not step 1), the count/action rows, and the
 * footer privacy line (card NYJEJRCBfBGQ).
 */
import type { GiftFlowState } from './hooks';
import { fmtDate, fmtDeadlineLine } from './types';

export function GiftHome({ g }: { g: GiftFlowState }) {
  const partner = g.partnerName;
  const inProg = g.inProgress;

  const openInProgress = () => {
    if (!inProg) return;
    // Tapping the in-progress block reopens PLAN when a pick exists.
    const target = inProg.picked ? 4 : (g.recs.some((r) => r.searchId === inProg.search.searchId && r.state !== 'rejected') ? 3 : 2);
    void g.openSearch(inProg.search.searchId, target);
  };

  const latestWithSaved = [...g.searches]
    .filter((s) => (g.savedBySearch[s.searchId] ?? 0) > 0)
    .sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
  const latestGiven = [...g.searches]
    .filter((s) => s.status === 'given')
    .sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;

  return (
    <div className="gf-home" data-testid="gift-home">
      {/* Primary card — the one way in. */}
      <section className="gf-primary" data-testid="gift-home-primary">
        <h4 className="gf-primary-title">Get a gift for {partner}</h4>
        <p className="gf-muted">
          A few quick questions, then I check her interests and your private list and shop within your budget.
        </p>
        <button
          type="button"
          className="pear-btn pear-btn-mustard"
          data-testid="gift-start"
          disabled={!g.partnerId || g.busy}
          onClick={g.start}
        >
          Start
        </button>
      </section>

      {/* In progress — reopens Plan, never step 1. */}
      {inProg && (
        <button
          type="button"
          className="gf-inprogress"
          data-testid="gift-inprogress"
          onClick={openInProgress}
        >
          <span className="gf-inprogress-occasion" data-testid="gift-inprogress-occasion">
            {inProg.search.occasionType}
            {inProg.search.occasionDate ? ` · ${fmtDate(inProg.search.occasionDate)}` : ''}
          </span>
          {inProg.picked ? (
            <span className="gf-inprogress-pick" data-testid="gift-inprogress-pick">
              Picked: {inProg.picked.title}. {fmtDeadlineLine(inProg.picked.deadline)}
            </span>
          ) : (
            <span className="gf-muted">No pick yet — continue the search</span>
          )}
        </button>
      )}

      {/* Rows with counts / actions. */}
      <ul className="gf-rows">
        <li>
          <button
            type="button"
            className="gf-row"
            data-testid="gift-row-saved"
            disabled={!latestWithSaved}
            onClick={() => latestWithSaved && void g.openSearch(latestWithSaved.searchId, 3)}
          >
            <span>Saved Ideas for {partner}</span>
            <span className="gf-row-count" data-testid="gift-saved-count">{g.savedCount}</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className="gf-row"
            data-testid="gift-row-history"
            disabled={!latestGiven}
            onClick={() => latestGiven && void g.openSearch(latestGiven.searchId, 4)}
          >
            <span>Gift history</span>
            <span className="gf-row-count" data-testid="gift-history-count">{g.historyCount}</span>
          </button>
        </li>
        <li>
          <button type="button" className="gf-row" data-testid="gift-row-defaults" onClick={() => void g.openDefaults()}>
            <span>Gift defaults (private)</span>
            <span className="gf-row-action">Edit</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className="gf-row"
            data-testid="gift-row-taste"
            onClick={() => g.onOpenUs?.()}
          >
            <span>My taste, kept in Us</span>
            <span className="gf-row-action">Open</span>
          </button>
        </li>
      </ul>

      <p className="gf-privacy" data-testid="gift-privacy-line">
        Only you can see anything about gifts for {partner}
      </p>
    </div>
  );
}
